import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings,
  User,
  Users,
  UserPlus,
  UserMinus,
  Mail,
  CheckSquare,
  ChevronRight,
  X,
  Send,
  Clock,
  Database,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Link2,
  MessageSquare,
  Calendar,
  Check,
  Smartphone,
  Crown,
  Sparkles,
  HelpCircle
} from 'lucide-react';
import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../utils/firebase';
import IOSShortcutSetupModal from '../components/IOSShortcutSetupModal';
import OnboardingWizard from '../components/onboarding/OnboardingWizard';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../hooks/useUserProfile';
import { useGoogleCalendar } from '../hooks/useGoogleCalendar';
import { useUsers } from '../hooks/useUsers';
import { useInvitations } from '../hooks/useInvitations';
import { useSubscription } from '../hooks/useSubscription';
import { runMigration, checkMigrationStatus } from '../utils/migration';

export default function SettingsPage() {
  const { user, userProfile } = useAuth();
  const { hasProAccess, displayStatus, isTrialing, trialDaysRemaining } = useSubscription();
  const {
    toggleTaskManagement,
    taskManagementEnabled,
    connectSlack,
    disconnectSlack,
    slackUserId,
    connectGmail,
    disconnectGmail,
    gmailConnected,
    gmailEmail,
    shortcutApiKey,
    generateShortcutApiKey
  } = useUserProfile();
  const {
    isSignedIn: gcalSignedIn,
    connectedEmail: gcalEmail,
    signIn: gcalSignIn,
    signOut: gcalSignOut,
    loading: gcalLoading,
    error: gcalError
  } = useGoogleCalendar();
  const { manager, supervisors, loading: hierarchyLoading } = useUsers();
  const {
    sentInvitations,
    receivedInvitations,
    inviteSupervisor,
    inviteManager,
    acceptInvitation,
    declineInvitation,
    cancelInvitation
  } = useInvitations();

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteDirection, setInviteDirection] = useState('supervisor');
  const [inviteError, setInviteError] = useState('');
  const [inviting, setInviting] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);

  // Slack connection state
  const [slackConnecting, setSlackConnecting] = useState(false);
  const [slackMessage, setSlackMessage] = useState('');

  // Gmail connection state
  const [gmailConnecting, setGmailConnecting] = useState(false);
  const [gmailMessage, setGmailMessage] = useState('');

  // iOS Shortcuts modal state
  const [showShortcutModal, setShowShortcutModal] = useState(false);

  // Migration state
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState('');
  const [migrationResults, setMigrationResults] = useState(null);
  const [migrationError, setMigrationError] = useState('');

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [resettingOnboarding, setResettingOnboarding] = useState(false);

  // Check if user is the primary admin (Jason)
  const isAdmin = user?.email === 'jason.brannon@entrusted.com';

  // Handle Slack and Gmail OAuth callback messages from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Handle Slack OAuth callback
    const slackStatus = params.get('slack');
    if (slackStatus === 'connected') {
      setSlackMessage('Slack connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setSlackMessage(''), 3000);
    } else if (slackStatus === 'error') {
      const errorMessage = params.get('message') || 'Failed to connect Slack';
      setSlackMessage(`Error: ${decodeURIComponent(errorMessage)}`);
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setSlackMessage(''), 5000);
    }

    // Handle Gmail OAuth callback
    const gmailStatus = params.get('gmail');
    if (gmailStatus === 'connected') {
      setGmailMessage('Gmail connected successfully! Emails labeled *AddToInbox will become tasks.');
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setGmailMessage(''), 5000);
    } else if (gmailStatus === 'error') {
      const errorMessage = params.get('message') || 'Failed to connect Gmail';
      setGmailMessage(`Error: ${decodeURIComponent(errorMessage)}`);
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setGmailMessage(''), 5000);
    }
  }, []);

  const handleConnectSlack = async () => {
    setSlackConnecting(true);
    setSlackMessage('');
    try {
      await connectSlack();
    } catch (error) {
      setSlackMessage(`Error: ${error.message}`);
      setSlackConnecting(false);
    }
  };

  const handleDisconnectSlack = async () => {
    try {
      await disconnectSlack();
      setSlackMessage('Slack disconnected');
      setTimeout(() => setSlackMessage(''), 3000);
    } catch (error) {
      setSlackMessage(`Error: ${error.message}`);
    }
  };

  const handleConnectGmail = async () => {
    setGmailConnecting(true);
    setGmailMessage('');
    try {
      await connectGmail();
    } catch (error) {
      setGmailMessage(`Error: ${error.message}`);
      setGmailConnecting(false);
    }
  };

  const handleDisconnectGmail = async () => {
    try {
      await disconnectGmail();
      setGmailMessage('Gmail disconnected');
      setTimeout(() => setGmailMessage(''), 3000);
    } catch (error) {
      setGmailMessage(`Error: ${error.message}`);
    }
  };

  const handleRunMigration = async () => {
    setMigrationRunning(true);
    setMigrationProgress('Starting migration...');
    setMigrationResults(null);
    setMigrationError('');

    try {
      const results = await runMigration((progress) => {
        setMigrationProgress(progress);
      }, user);
      setMigrationResults(results);
      setMigrationProgress('Migration complete!');
    } catch (error) {
      console.error('Migration failed:', error);
      setMigrationError(error.message);
      setMigrationProgress('');
    } finally {
      setMigrationRunning(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviteError('');
    setInviting(true);

    try {
      if (inviteDirection === 'supervisor') {
        await inviteSupervisor(inviteEmail.trim());
      } else {
        await inviteManager(inviteEmail.trim());
      }
      setInviteEmail('');
      setShowInviteForm(false);
    } catch (error) {
      setInviteError(error.message);
    } finally {
      setInviting(false);
    }
  };

  const handleRestartOnboarding = async () => {
    if (!user?.uid) return;

    setResettingOnboarding(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        'onboarding.completedAt': deleteField(),
        'onboarding.skippedAt': deleteField(),
        'onboarding.currentStep': 'welcome',
        'onboarding.completedSteps': [],
        'onboarding.brainDumpCount': 0,
        updatedAt: new Date()
      });
      setShowOnboarding(true);
    } catch (error) {
      console.error('Error resetting onboarding:', error);
    } finally {
      setResettingOnboarding(false);
    }
  };

  return (
    <div className="pb-20 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-accent-muted">
          <Settings size={20} className="text-accent" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-text-primary">Settings</h1>
          <p className="text-xs text-text-tertiary">Manage your profile and team</p>
        </div>
      </div>

      {/* Subscription Section */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
          <Crown size={14} />
          Subscription
        </h2>
        <Link
          to="/billing"
          className="block bg-surface-card rounded-xl border border-border p-4 hover:border-accent/50 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                hasProAccess
                  ? 'bg-gradient-to-br from-accent/20 to-orange-500/20'
                  : 'bg-surface-elevated'
              }`}>
                <Crown size={24} className={hasProAccess ? 'text-accent' : 'text-text-tertiary'} />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">{displayStatus}</p>
                {isTrialing && trialDaysRemaining !== null && (
                  <p className="text-xs text-warning">{trialDaysRemaining} days remaining</p>
                )}
                {!hasProAccess && !isTrialing && (
                  <p className="text-xs text-text-tertiary">Upgrade to unlock all features</p>
                )}
              </div>
            </div>
            <ChevronRight size={16} className="text-text-tertiary" />
          </div>
        </Link>
      </section>

      {/* Help & Setup Section */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
          <HelpCircle size={14} />
          Help & Setup
        </h2>
        <div className="bg-surface-card rounded-xl border border-border overflow-hidden">
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent/20 to-orange-500/20 flex items-center justify-center">
                <Sparkles size={20} className="text-accent" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Setup Guide</p>
                <p className="text-xs text-text-tertiary">Learn the GTD system with a guided walkthrough</p>
              </div>
            </div>
            <button
              onClick={handleRestartOnboarding}
              disabled={resettingOnboarding}
              className="w-full py-2.5 px-4 bg-gradient-to-r from-accent to-orange-500 text-white rounded-lg text-sm font-medium border-none cursor-pointer hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {resettingOnboarding ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Restart Setup Guide
                </>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Profile Section */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
          <User size={14} />
          Profile
        </h2>
        <div className="bg-surface-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-accent-muted flex items-center justify-center">
              <span className="text-lg font-semibold text-accent">
                {userProfile?.firstName?.[0] || user?.email?.[0]?.toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary">
                {userProfile?.displayName || user?.displayName}
              </p>
              <p className="text-xs text-text-tertiary">{user?.email}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
          <CheckSquare size={14} />
          Features
        </h2>
        <div className="bg-surface-card rounded-xl border border-border overflow-hidden">
          <label className="flex items-center justify-between p-4 cursor-pointer hover:bg-surface-hover transition-colors">
            <div>
              <p className="text-sm text-text-primary">Task Management</p>
              <p className="text-xs text-text-tertiary">
                Enable Tasks, Schedule, and Daily Review tabs
              </p>
            </div>
            <input
              type="checkbox"
              checked={taskManagementEnabled}
              onChange={(e) => toggleTaskManagement(e.target.checked)}
              className="w-5 h-5 rounded accent-accent cursor-pointer"
            />
          </label>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
          <Link2 size={14} />
          Integrations
        </h2>
        <div className="bg-surface-card rounded-xl border border-border overflow-hidden">
          {/* Slack Integration */}
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#4A154B] flex items-center justify-center">
                <MessageSquare size={20} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Slack</p>
                <p className="text-xs text-text-tertiary">
                  {slackUserId
                    ? `Connected • Use /inbox to add tasks`
                    : 'Add tasks to inbox with /inbox command'}
                </p>
              </div>
            </div>

            {slackUserId ? (
              <button
                onClick={handleDisconnectSlack}
                className="px-3 py-1.5 text-xs font-medium text-red-400 bg-red-500/10 rounded-lg border-none cursor-pointer hover:bg-red-500/20 transition-all"
              >
                Disconnect
              </button>
            ) : (
              <button
                onClick={handleConnectSlack}
                disabled={slackConnecting}
                className="px-4 py-2 text-xs font-medium text-white bg-[#4A154B] rounded-lg border-none cursor-pointer hover:bg-[#3a1039] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
              >
                {slackConnecting ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    Connecting...
                  </>
                ) : (
                  'Connect Slack'
                )}
              </button>
            )}
          </div>

          {/* Slack status message */}
          {slackMessage && (
            <div className={`px-4 py-2 text-xs border-t border-border ${
              slackMessage.startsWith('Error')
                ? 'bg-red-500/10 text-red-400'
                : 'bg-green-500/10 text-green-500'
            }`}>
              {slackMessage}
            </div>
          )}

          {/* Google Calendar Integration */}
          <div className="flex items-center justify-between p-4 border-t border-border">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${gcalSignedIn ? 'bg-green-500/10' : 'bg-surface-elevated'}`}>
                <Calendar size={20} className={gcalSignedIn ? 'text-green-500' : 'text-text-tertiary'} />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Google Calendar</p>
                <p className="text-xs text-text-tertiary">
                  {gcalSignedIn
                    ? `Connected • ${gcalEmail || 'Syncing time blocks'}`
                    : 'Sync time blocks from your calendar'}
                </p>
              </div>
            </div>

            {gcalLoading ? (
              <Loader2 size={16} className="text-accent animate-spin" />
            ) : gcalSignedIn ? (
              <button
                onClick={gcalSignOut}
                className="px-3 py-1.5 text-xs font-medium text-red-400 bg-red-500/10 rounded-lg border-none cursor-pointer hover:bg-red-500/20 transition-all"
              >
                Disconnect
              </button>
            ) : (
              <button
                onClick={gcalSignIn}
                className="px-4 py-2 text-xs font-medium text-white bg-blue-500 rounded-lg border-none cursor-pointer hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Connect
              </button>
            )}
          </div>

          {/* Google Calendar status */}
          {gcalSignedIn && (
            <div className="px-4 py-2 text-xs border-t border-border bg-green-500/10 text-green-500 flex items-center gap-1.5">
              <Check size={12} />
              Calendar events synced
            </div>
          )}

          {gcalError && (
            <div className="px-4 py-2 text-xs border-t border-border bg-red-500/10 text-red-400">
              {gcalError}
            </div>
          )}


          {/* Gmail Integration */}
          <div className="flex items-center justify-between p-4 border-t border-border">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${gmailConnected ? 'bg-green-500/10' : 'bg-red-500'}`}>
                <Mail size={20} className={gmailConnected ? 'text-green-500' : 'text-white'} />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Gmail</p>
                <p className="text-xs text-text-tertiary">
                  {gmailConnected
                    ? `Connected • ${gmailEmail}`
                    : 'Label emails *AddToInbox to create tasks'}
                </p>
              </div>
            </div>

            {gmailConnected ? (
              <button
                onClick={handleDisconnectGmail}
                className="px-3 py-1.5 text-xs font-medium text-red-400 bg-red-500/10 rounded-lg border-none cursor-pointer hover:bg-red-500/20 transition-all"
              >
                Disconnect
              </button>
            ) : (
              <button
                onClick={handleConnectGmail}
                disabled={gmailConnecting}
                className="px-4 py-2 text-xs font-medium text-white bg-red-500 rounded-lg border-none cursor-pointer hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
              >
                {gmailConnecting ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    Connecting...
                  </>
                ) : (
                  'Connect Gmail'
                )}
              </button>
            )}
          </div>

          {/* Gmail status message */}
          {gmailMessage && (
            <div className={`px-4 py-2 text-xs border-t border-border ${
              gmailMessage.startsWith('Error')
                ? 'bg-red-500/10 text-red-400'
                : 'bg-green-500/10 text-green-500'
            }`}>
              {gmailMessage}
            </div>
          )}

          {gmailConnected && (
            <div className="px-4 py-2 text-xs border-t border-border bg-green-500/10 text-green-500 flex items-center gap-1.5">
              <Check size={12} />
              Emails labeled *AddToInbox become tasks instantly
            </div>
          )}

          {/* iOS Shortcuts Integration */}
          <div className="flex items-center justify-between p-4 border-t border-border">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${shortcutApiKey ? 'bg-green-500/10' : 'bg-gradient-to-br from-pink-500 to-orange-400'}`}>
                <Smartphone size={20} className={shortcutApiKey ? 'text-green-500' : 'text-white'} />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">iOS Shortcuts</p>
                <p className="text-xs text-text-tertiary">
                  {shortcutApiKey
                    ? 'Configured • Add tasks with Siri'
                    : 'Add tasks from your iPhone with Siri'}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowShortcutModal(true)}
              className={`px-4 py-2 text-xs font-medium rounded-lg border-none cursor-pointer transition-all ${
                shortcutApiKey
                  ? 'text-text-secondary bg-surface-hover hover:bg-surface-elevated'
                  : 'text-white bg-gradient-to-r from-pink-500 to-orange-400 hover:opacity-90'
              }`}
            >
              {shortcutApiKey ? 'View Setup' : 'Set Up'}
            </button>
          </div>
        </div>
      </section>

      {/* iOS Shortcuts Setup Modal */}
      <IOSShortcutSetupModal
        isOpen={showShortcutModal}
        onClose={() => setShowShortcutModal(false)}
        apiKey={shortcutApiKey}
        onGenerateKey={generateShortcutApiKey}
      />

      {/* Onboarding Wizard */}
      {showOnboarding && (
        <OnboardingWizard
          onComplete={() => setShowOnboarding(false)}
          onSkip={() => setShowOnboarding(false)}
        />
      )}

      {/* Team Hierarchy Section */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-text-secondary flex items-center gap-2">
            <Users size={14} />
            Team Hierarchy
          </h2>
          <button
            onClick={() => setShowInviteForm(!showInviteForm)}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-accent bg-accent-muted rounded-lg border-none cursor-pointer hover:bg-accent/20 transition-all"
          >
            <UserPlus size={12} />
            Invite
          </button>
        </div>

        {/* Invite Form */}
        {showInviteForm && (
          <form onSubmit={handleInvite} className="bg-surface-card rounded-xl border border-border p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Mail size={14} className="text-text-tertiary" />
              <span className="text-xs text-text-tertiary">Send invitation</span>
            </div>

            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => setInviteDirection('supervisor')}
                className={`flex-1 py-2 px-3 text-xs rounded-lg border-none cursor-pointer transition-all ${
                  inviteDirection === 'supervisor'
                    ? 'bg-accent text-white'
                    : 'bg-surface-hover text-text-secondary hover:bg-surface-hover/80'
                }`}
              >
                Add Supervisor
              </button>
              <button
                type="button"
                onClick={() => setInviteDirection('manager')}
                className={`flex-1 py-2 px-3 text-xs rounded-lg border-none cursor-pointer transition-all ${
                  inviteDirection === 'manager'
                    ? 'bg-accent text-white'
                    : 'bg-surface-hover text-text-secondary hover:bg-surface-hover/80'
                }`}
              >
                Add Manager
              </button>
            </div>

            <div className="flex gap-2">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="email@example.com"
                className="flex-1 py-2 px-3 bg-surface-elevated border border-border rounded-lg text-sm text-text-primary placeholder:text-text-muted outline-none focus:border-accent"
              />
              <button
                type="submit"
                disabled={inviting || !inviteEmail.trim()}
                className="px-4 py-2 bg-accent text-white rounded-lg text-xs font-medium border-none cursor-pointer hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Send size={14} />
              </button>
            </div>

            {inviteError && (
              <p className="mt-2 text-xs text-red-400">{inviteError}</p>
            )}

            <p className="mt-2 text-[10px] text-text-muted">
              {inviteDirection === 'supervisor'
                ? 'They will become your supervisor (report to you)'
                : 'They will become your manager (you report to them)'}
            </p>
          </form>
        )}

        {/* Manager */}
        <div className="bg-surface-card rounded-xl border border-border overflow-hidden mb-3">
          <div className="px-4 py-2 bg-surface-hover border-b border-border">
            <span className="text-[10px] font-semibold text-text-tertiary uppercase tracking-wide">
              Your Manager
            </span>
          </div>
          <div className="p-4">
            {hierarchyLoading ? (
              <p className="text-xs text-text-muted">Loading...</p>
            ) : manager ? (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <span className="text-xs font-medium text-purple-400">
                    {manager.firstName?.[0] || manager.email?.[0]?.toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-text-primary">{manager.displayName || manager.firstName}</p>
                  <p className="text-[10px] text-text-tertiary">{manager.email}</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-text-muted">No manager assigned</p>
            )}
          </div>
        </div>

        {/* Supervisors */}
        <div className="bg-surface-card rounded-xl border border-border overflow-hidden">
          <div className="px-4 py-2 bg-surface-hover border-b border-border">
            <span className="text-[10px] font-semibold text-text-tertiary uppercase tracking-wide">
              Your Supervisors ({supervisors.length})
            </span>
          </div>
          <div className="p-4">
            {hierarchyLoading ? (
              <p className="text-xs text-text-muted">Loading...</p>
            ) : supervisors.length > 0 ? (
              <div className="space-y-3">
                {supervisors.map((supervisor) => (
                  <div key={supervisor.id} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent-muted flex items-center justify-center">
                      <span className="text-xs font-medium text-accent">
                        {supervisor.firstName?.[0] || supervisor.email?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-text-primary">
                        {supervisor.displayName || supervisor.firstName}
                      </p>
                      <p className="text-[10px] text-text-tertiary">{supervisor.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-text-muted">No supervisors yet</p>
            )}
          </div>
        </div>
      </section>

      {/* Pending Invitations Section */}
      {(receivedInvitations.length > 0 || sentInvitations.length > 0) && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
            <Clock size={14} />
            Pending Invitations
          </h2>

          {/* Received Invitations */}
          {receivedInvitations.length > 0 && (
            <div className="bg-surface-card rounded-xl border border-accent/30 overflow-hidden mb-3">
              <div className="px-4 py-2 bg-accent-muted border-b border-accent/30">
                <span className="text-[10px] font-semibold text-accent uppercase tracking-wide">
                  Invitations for You
                </span>
              </div>
              <div className="p-4 space-y-3">
                {receivedInvitations.map((invitation) => (
                  <div key={invitation.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-text-primary">{invitation.inviterName}</p>
                      <p className="text-[10px] text-text-tertiary">
                        Wants you as their {invitation.direction === 'supervisor' ? 'supervisor' : 'supervisor'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => acceptInvitation(invitation.id)}
                        className="px-3 py-1.5 bg-success/10 text-success rounded-lg text-xs font-medium border-none cursor-pointer hover:bg-success/20 transition-all"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => declineInvitation(invitation.id)}
                        className="px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg text-xs font-medium border-none cursor-pointer hover:bg-red-500/20 transition-all"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sent Invitations */}
          {sentInvitations.length > 0 && (
            <div className="bg-surface-card rounded-xl border border-border overflow-hidden">
              <div className="px-4 py-2 bg-surface-hover border-b border-border">
                <span className="text-[10px] font-semibold text-text-tertiary uppercase tracking-wide">
                  Sent by You
                </span>
              </div>
              <div className="p-4 space-y-3">
                {sentInvitations.map((invitation) => (
                  <div key={invitation.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-text-primary">{invitation.inviteeEmail}</p>
                      <p className="text-[10px] text-text-tertiary">
                        Invited as {invitation.direction}
                      </p>
                    </div>
                    <button
                      onClick={() => cancelInvitation(invitation.id)}
                      className="p-1.5 text-text-muted hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all border-none bg-transparent cursor-pointer"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Admin Migration Section - Only visible to Jason */}
      {isAdmin && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
            <Database size={14} />
            Data Migration
          </h2>
          <div className="bg-surface-card rounded-xl border border-border overflow-hidden">
            <div className="px-4 py-2 bg-yellow-500/10 border-b border-yellow-500/30">
              <span className="text-[10px] font-semibold text-yellow-500 uppercase tracking-wide">
                Admin Only
              </span>
            </div>
            <div className="p-4">
              <p className="text-xs text-text-secondary mb-3">
                Run this migration to convert existing data to the new multi-tenant format.
                This will add user IDs to tasks, projects, habits, and 1:1 relationships.
              </p>

              {migrationError && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 rounded-lg mb-3">
                  <AlertCircle size={14} className="text-red-400" />
                  <p className="text-xs text-red-400">{migrationError}</p>
                </div>
              )}

              {migrationProgress && !migrationResults && (
                <div className="flex items-center gap-2 p-3 bg-accent-muted rounded-lg mb-3">
                  <Loader2 size={14} className="text-accent animate-spin" />
                  <p className="text-xs text-accent">{migrationProgress}</p>
                </div>
              )}

              {migrationResults && (
                <div className="p-3 bg-green-500/10 rounded-lg mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 size={14} className="text-green-500" />
                    <p className="text-xs font-medium text-green-500">Migration Complete!</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[10px] text-text-tertiary">
                    <div>Users: {migrationResults.users.created} created</div>
                    <div>Tasks: {migrationResults.tasks.migrated} migrated</div>
                    <div>Projects: {migrationResults.projects.migrated} migrated</div>
                    <div>Habits: {migrationResults.links.migrated} migrated</div>
                    <div>1:1s: {migrationResults.relationships.migrated} migrated</div>
                    <div>Topics: {migrationResults.topics.migrated} migrated</div>
                    <div>Actions: {migrationResults.actionItems.migrated} migrated</div>
                    <div>Goals: {migrationResults.goals.migrated} migrated</div>
                    <div>Policies: {migrationResults.policies.migrated} migrated</div>
                    <div>Time Blocks: {migrationResults.timeBlocks.migrated} migrated</div>
                  </div>
                </div>
              )}

              <button
                onClick={handleRunMigration}
                disabled={migrationRunning}
                className="w-full py-2 px-4 bg-yellow-500 text-white rounded-lg text-xs font-medium border-none cursor-pointer hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {migrationRunning ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Running Migration...
                  </>
                ) : (
                  <>
                    <Database size={14} />
                    Run Migration
                  </>
                )}
              </button>

              <p className="mt-2 text-[10px] text-text-muted">
                Safe to run multiple times - already migrated data will be skipped.
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
